import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { Badge, EmptyState, Switch, TableSkeleton } from "../../../components/ui";
import {
  apiMessage,
  getAutomations,
  getReminders,
  runAutomation,
  updateAutomation,
} from "../../../services/notifications";
import { PhonePreview } from "./Composer";
import ReminderAutomation from "./ReminderAutomation";

// Monday first, values as in JS (0 = Sunday)
const DAYS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 0, label: "Sun" },
];

const TIMEZONES = [
  "Africa/Abidjan",
  "Africa/Lagos",
  "Africa/Douala",
  "Africa/Kinshasa",
  "Africa/Johannesburg",
  "Europe/Paris",
  "America/New_York",
];

const pad = (n) => String(n).padStart(2, "0");

/** Text fields of an automation's settings, with their label. */
const TEXT_FIELDS = [
  { key: "title", label: "Title" },
  { key: "bodyOne", label: "Message (1 missed video)" },
  { key: "bodyMany", label: "Message (several missed videos)" },
];

const AutomationCard = ({ automation, onSaved }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(automation);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmRun, setConfirmRun] = useState(false);
  const [english, setEnglish] = useState(false);

  useEffect(() => setForm(automation), [automation]);

  const dirty = JSON.stringify(form) !== JSON.stringify(automation);
  const set = (changes) => setForm((f) => ({ ...f, ...changes }));
  const setSetting = (key, value) => setForm((f) => ({ ...f, settings: { ...f.settings, [key]: value } }));
  const toggleDay = (day) =>
    set({ days: form.days.includes(day) ? form.days.filter((d) => d !== day) : [...form.days, day] });

  const save = async (changes = {}) => {
    setSaving(true);
    try {
      const saved = await updateAutomation(automation.key, {
        enabled: form.enabled,
        days: form.days,
        hour: form.hour,
        minute: form.minute,
        timezone: form.timezone,
        settings: form.settings,
        ...changes,
      });
      onSaved(saved);
      toast.success(t("Automation saved"));
    } catch (error) {
      toast.error(apiMessage(error, t("Something went wrong")));
    }
    setSaving(false);
  };

  const showPreview = async () => {
    if (dirty) await save();
    setPreviewing(true);
    try {
      setPreview(await runAutomation(automation.key, { dryRun: true }));
    } catch (error) {
      toast.error(apiMessage(error, t("Something went wrong")));
    }
    setPreviewing(false);
  };

  const runNow = async () => {
    try {
      const result = await runAutomation(automation.key);
      setPreview(result);
      if (result.recipients) {
        toast.success(t("Sent to {{count}} user(s)", { count: result.stats?.success ?? result.recipients }));
      } else {
        toast.info(t("Nobody to notify: no missed video right now"));
      }
      onSaved(null);
    } catch (error) {
      toast.error(apiMessage(error, t("Something went wrong")));
    }
  };

  const suffix = english ? "_en" : "";
  const sample = preview?.samples?.[0];

  return (
    <section className="ui-card notif-automation">
      <header className="notif-automation-head">
        <span className="notif-automation-icon" aria-hidden="true">
          <i className="ri-calendar-event-line"></i>
        </span>
        <div className="notif-automation-title">
          <h2>{t(automation.label)}</h2>
          <p>{t(automation.description)}</p>
        </div>
        <Switch
          checked={form.enabled}
          onChange={(enabled) => {
            set({ enabled });
            save({ enabled });
          }}
          label={t("Enabled")}
          onLabel={t("Enabled")}
          offLabel={t("Disabled")}
        />
      </header>

      <div className="notif-automation-body">
        <div className="notif-automation-settings">
          <div className="notif-section">
            <span className="ui-label">{t("Days")}</span>
            <div className="ui-chips">
              {DAYS.map((day) => (
                <button
                  key={day.value}
                  type="button"
                  className="ui-chip"
                  aria-pressed={form.days.includes(day.value)}
                  onClick={() => toggleDay(day.value)}
                >
                  {t(day.label)}
                </button>
              ))}
            </div>
          </div>

          <div className="ui-form">
            <div className="ui-field">
              <label htmlFor={`${automation.key}-time`}>{t("Time")}</label>
              <input
                id={`${automation.key}-time`}
                type="time"
                className="ui-input"
                value={`${pad(form.hour)}:${pad(form.minute)}`}
                onChange={(e) => {
                  const [hour, minute] = e.target.value.split(":").map(Number);
                  if (!Number.isNaN(hour)) set({ hour, minute: minute || 0 });
                }}
              />
            </div>
            <div className="ui-field">
              <label htmlFor={`${automation.key}-tz`}>{t("Time zone")}</label>
              <select
                id={`${automation.key}-tz`}
                className="ui-input"
                value={form.timezone}
                onChange={(e) => set({ timezone: e.target.value })}
              >
                {[...new Set([form.timezone, ...TIMEZONES])].map((tz) => (
                  <option key={tz} value={tz}>
                    {tz.replace("_", " ")}
                  </option>
                ))}
              </select>
            </div>
            <div className="ui-field">
              <label htmlFor={`${automation.key}-lookback`}>{t("Videos published in the last (days)")}</label>
              <input
                id={`${automation.key}-lookback`}
                type="number"
                min={1}
                max={30}
                className="ui-input"
                value={form.settings.lookbackDays}
                onChange={(e) => setSetting("lookbackDays", Number(e.target.value))}
              />
            </div>
            <div className="ui-field notif-automation-switch">
              <Switch
                checked={Boolean(form.settings.includeShorts)}
                onChange={(value) => setSetting("includeShorts", value)}
                label={t("Include shorts")}
                onLabel={t("Include shorts")}
                offLabel={t("Include shorts")}
              />
            </div>
          </div>

          <div className="notif-section">
            <div className="notif-automation-texts-head">
              <span className="ui-label">{t("Texts")}</span>
              <div className="ui-chips">
                <button type="button" className="ui-chip" aria-pressed={!english} onClick={() => setEnglish(false)}>
                  {t("French")}
                </button>
                <button type="button" className="ui-chip" aria-pressed={english} onClick={() => setEnglish(true)}>
                  {t("English")}
                </button>
              </div>
            </div>
            <div className="ui-form">
              {TEXT_FIELDS.map((field) => (
                <div key={field.key} className="ui-field ui-field-full">
                  <label htmlFor={`${automation.key}-${field.key}${suffix}`}>{t(field.label)}</label>
                  <input
                    id={`${automation.key}-${field.key}${suffix}`}
                    className="ui-input"
                    value={form.settings[`${field.key}${suffix}`] || ""}
                    onChange={(e) => setSetting(`${field.key}${suffix}`, e.target.value)}
                  />
                </div>
              ))}
            </div>
            <p className="ui-hint">
              {t("Variables: {video} title of the video, {autres} number of other videos, {nombre} total, {prenom} first name.")}
            </p>
          </div>
        </div>

        <aside className="notif-preview-col">
          <PhonePreview
            title={sample?.title || form.settings[`title${suffix}`]}
            body={
              sample?.body ||
              (form.settings[`bodyMany${suffix}`] || "")
                .replace("{video}", "Le Royaume")
                .replace("{autres}", "3")
                .replace("{nombre}", "4")
                .replace("{prenom}", "Awa")
            }
          />
          {preview && (
            <div className="notif-automation-result">
              <div>
                <strong>{preview.recentVideos}</strong>
                <span>{t("recent videos")}</span>
              </div>
              <div>
                <strong>{preview.reachableUsers}</strong>
                <span>{t("reachable users")}</span>
              </div>
              <div>
                <strong>{preview.recipients}</strong>
                <span>{preview.stats ? t("notified") : t("would be notified")}</span>
              </div>
            </div>
          )}
        </aside>
      </div>

      <footer className="notif-automation-foot">
        <span className="ui-hint">
          {form.enabled ? (
            <Badge color="green" dot>
              {t("Next: {{when}}", { when: automation.schedule })}
            </Badge>
          ) : (
            <Badge color="slate" dot>
              {t("Disabled")}
            </Badge>
          )}
          {automation.lastRunAt && (
            <span className="notif-last-run">
              {t("Last sent")} : {new Date(automation.lastRunAt).toLocaleString()}
            </span>
          )}
        </span>
        <div className="notif-actions">
          <button type="button" className="ui-btn ui-btn-secondary" disabled={previewing} onClick={showPreview}>
            <i className="ri-eye-line" aria-hidden="true"></i>
            {previewing ? t("Calculating…") : t("Preview recipients")}
          </button>
          <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setConfirmRun(true)}>
            <i className="ri-send-plane-line" aria-hidden="true"></i>
            {t("Send now")}
          </button>
          <button type="button" className="ui-btn" disabled={!dirty || saving} onClick={() => save()}>
            {saving ? t("Saving…") : t("Save")}
          </button>
        </div>
      </footer>

      <ConfirmDialog
        open={confirmRun}
        onClose={() => setConfirmRun(false)}
        onConfirm={runNow}
        confirmLabel={t("Send now")}
      >
        <strong>{t("Send this reminder now?")}</strong>
        <p>{t("Each user who missed videos receives it right away, outside the usual schedule.")}</p>
      </ConfirmDialog>
    </section>
  );
};

const Automations = ({ onChange }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [reminders, setReminders] = useState([]);

  const load = () => {
    getAutomations().then(setItems);
    // Relances par notification et/ou e-mail (relance du quiz…)
    getReminders()
      .then(setReminders)
      .catch(() => setReminders([]));
  };
  useEffect(() => {
    load();
  }, []);

  const saved = (automation) => {
    if (automation) setItems((list) => list.map((a) => (a.key === automation.key ? automation : a)));
    else load();
    onChange && onChange();
  };
  const savedReminder = (reminder) => {
    if (reminder) setReminders((list) => list.map((a) => (a.key === reminder.key ? reminder : a)));
    else load();
    onChange && onChange();
  };

  if (!items) return <TableSkeleton rows={3} />;
  if (!items.length && !reminders.length) return <EmptyState>{t("No automation")}</EmptyState>;
  return (
    <div className="notif-automations">
      {items.map((automation) => (
        <AutomationCard key={automation.key} automation={automation} onSaved={saved} />
      ))}
      {reminders.map((reminder) => (
        <ReminderAutomation key={reminder.key} automation={reminder} onSaved={savedReminder} />
      ))}
      <p className="ui-hint notif-automations-note">
        <i className="ri-information-line" aria-hidden="true"></i>{" "}
        {t("Automatic and scheduled sends run on the production server only (NOTIFICATION_JOBS_ENABLED=true).")}
      </p>
    </div>
  );
};

export default Automations;
